import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Fish, MapPin, Star, Flame, ArrowRight, Copy, Loader2, Tent } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * iter121 — Fishing × Camping launch reel.
 *
 * /share/fishing-camping renders three 9:16 scenes:
 *   1. Hook: "Love camping? Love fishing? Why not combine the two?"
 *   2. Example pairing: campsite ←→ nearest named fishery + distance
 *   3. CTA: "Find your pair on camp-compass.uk"
 *
 * The example pairing is fetched live so it auto-rotates as new fisheries
 * are discovered against new campsites — no redeploy needed.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");

function Sticker({ children, color = "#fff", textColor = "#0e0e0e" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.22em] mono"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="mb-2 text-[11px] mono uppercase tracking-[0.25em] text-[#888]">{label}</div>
      <section
        className="relative w-[280px] sm:w-[310px] overflow-hidden shadow-2xl text-white"
        style={{ aspectRatio: "9 / 16", background: bg }}
        data-testid={testid}
      >
        {children}
        <div
          className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/65 backdrop-blur-sm text-white text-[10px] sm:text-[11px] font-bold mono uppercase tracking-[0.22em] text-center pointer-events-none z-10"
          aria-hidden="true"
        >
          🌐 camp-compass.uk
        </div>
      </section>
    </div>
  );
}

function CopyBlock({ title, body, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      toast.success("Caption copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Couldn't copy — long-press to copy manually"); }
  };
  return (
    <div className="bg-[#0e0e0e] text-white border border-[#1f1f1f] p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="font-display text-sm">{title}</div>
        <button
          onClick={copy}
          className="text-xs mono uppercase tracking-wider text-[#0891b2] hover:underline inline-flex items-center gap-1"
        >
          <Copy className="w-3.5 h-3.5" /> {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap font-sans text-xs sm:text-sm text-[#ddd] leading-relaxed">{body}</pre>
    </div>
  );
}

export default function FishingCampingReel() {
  // Pull a live example: a campsite with a known nearby fishery.
  // We try Orcaber first (Yorkshire Dales, plenty of fisheries around),
  // and fall back to Frowen Fields if that fails for any reason.
  const [example, setExample] = useState(null);
  const [exampleErr, setExampleErr] = useState(false);

  useEffect(() => {
    (async () => {
      // Try a Cotswolds Water Park campsite first since the OSM fishery
      // density there is the highest in the UK (Lechlade, Bibury Trout,
      // Flaxlands, Aspen Lake all within 20 km). Fall back to other big
      // sites if those aren't ingested or have zero nearby.
      for (const id of [
        "cs_pu_456f5fdbd5",          // Cotswold Lakes Camping, Gloucestershire
        "cs_pu_5df96e7bb3",          // Mayfield Park, Gloucestershire
        "cs_osm_1031838811",         // Orcaber Farm Camping, Yorkshire Dales
        "cs_direct_frowen_fields",  // Frowen Fields, Carmarthenshire
        "cs_724b96e72a",             // Great Langdale, Lake District
      ]) {
        try {
          const [siteRes, fishRes] = await Promise.all([
            api.get(`/campsites/${id}`),
            api.get(`/campsites/${id}/fisheries`),
          ]);
          const fish = (fishRes.data?.fisheries || [])[0];
          if (fish) {
            setExample({ site: siteRes.data, fishery: fish, total: fishRes.data?.total || 0 });
            return;
          }
        } catch { /* try next */ }
      }
      setExampleErr(true);
    })();
  }, []);

  const captionLong = `🏕 Love camping? 🎣 Love fishing? Why not combine the two?

Every campsite on CampCompass UK now shows you fisheries within 10 km — see exactly which lakes, ponds and day-ticket waters you can fish from your pitch.

${example ? `Example: pitch at **${example.site.name}** in ${(example.site.region || "").split(",")[0]} and you're ${example.fishery.distance_from_campsite_km} km from **${example.fishery.name}** 🎣` : "Tap the link, search any campsite, and the fisheries within 10 km are listed alongside the walks."}

🌐 ${PUBLIC_URL}

22,000+ UK campsites · all activities · all campers welcome.

#ukcamping #fishing #fishingtrip #camping #carpfishing #flyfishing #ukfishing`;

  const captionShort = `🏕🎣 Camp + fish, paired up

Every UK campsite on CampCompass now lists fisheries within 10 km of your pitch.

🌐 ${PUBLIC_URL}

#fishing #ukcamping`;

  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white pb-24" data-testid="fishing-camping-reel">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-6">
        <Link to="/" className="text-xs mono text-[#888] hover:text-[#0891b2] uppercase tracking-wider">← Back</Link>
        <div className="mt-3 inline-flex items-center gap-2 text-[11px] mono uppercase tracking-[0.25em] text-[#0891b2]">
          <Fish className="w-3.5 h-3.5" fill="currentColor" /> Camp × Fish launch reel
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2">Love camping? Love fishing?</h1>
        <p className="text-sm text-[#bbb] mt-2 max-w-xl">
          Three 9:16 scenes — screen-record and post on Instagram, TikTok or Facebook.
          The pairing example below is fetched live so it stays up to date as our
          fishery database grows.
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-items-center">
          {/* SCENE 1 — THE HOOK */}
          <Scene
            label="Scene 1 · the hook"
            testid="fc-scene-1"
            bg="linear-gradient(180deg, #0c4a6e 0%, #0891b2 55%, #fde047 100%)"
          >
            <div className="p-5 pt-7 text-center">
              <Sticker color="#fde047">
                <Flame className="w-3 h-3" /> NEW · iter121
              </Sticker>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
              <div className="text-[26px] font-display leading-[1.05] drop-shadow-[0_3px_12px_rgba(0,0,0,0.7)]">
                Love<br />camping? 🏕
              </div>
              <div className="text-[26px] font-display leading-[1.05] mt-4 drop-shadow-[0_3px_12px_rgba(0,0,0,0.7)]">
                Love<br />fishing? 🎣
              </div>
              <div className="mt-6 bg-white text-[#0c4a6e] inline-block px-4 py-2.5">
                <div className="font-display text-base leading-tight">Why not combine the two?</div>
              </div>
              <div className="mt-5 mono text-[10px] uppercase tracking-[0.22em] text-white/95">
                Tap to see how ↓
              </div>
            </div>
          </Scene>

          {/* SCENE 2 — THE LIVE EXAMPLE */}
          <Scene
            label="Scene 2 · the pairing"
            testid="fc-scene-2"
            bg="linear-gradient(180deg, #0e0e0e 0%, #134e4a 60%, #0891b2 100%)"
          >
            <div className="p-5 pt-7 text-center">
              <Sticker color="#0891b2" textColor="#ffffff">
                <MapPin className="w-3 h-3" /> Live pairing
              </Sticker>
            </div>
            {!example && !exampleErr && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#0891b2]" />
              </div>
            )}
            {exampleErr && (
              <div className="absolute inset-0 flex items-center justify-center text-center px-4">
                <div className="text-sm">No nearby fishery found in our preview — try this scene live on production after a redeploy.</div>
              </div>
            )}
            {example && (
              <div className="absolute inset-0 px-4 pt-16 pb-14 flex flex-col items-center justify-between text-center">
                <div className="space-y-1">
                  <Sticker color="#fde047">
                    <Tent className="w-3 h-3" /> Pitch here
                  </Sticker>
                  <div className="font-display text-[20px] leading-tight mt-2 drop-shadow-[0_3px_12px_rgba(0,0,0,0.7)]">
                    {example.site.name}
                  </div>
                  <div className="mono text-[10px] uppercase tracking-wider text-white/90">
                    {(example.site.region || "UK").split(",")[0]}
                  </div>
                  {typeof example.site.rating === "number" && example.site.rating > 0 && (
                    <div className="inline-flex items-center gap-1 mt-1 text-[12px] text-[#fde047]">
                      <Star className="w-3 h-3" fill="currentColor" /> {example.site.rating.toFixed(1)}
                    </div>
                  )}
                </div>
                {/* Connector arrow */}
                <div className="flex flex-col items-center my-2">
                  <div className="w-px h-7 bg-[#0891b2]" />
                  <div className="bg-white text-[#0c4a6e] px-3 py-1.5 font-display text-[18px]">
                    {example.fishery.distance_from_campsite_km} km
                  </div>
                  <div className="w-px h-7 bg-[#0891b2]" />
                </div>
                <div className="space-y-1">
                  <Sticker color="#0891b2" textColor="#ffffff">
                    <Fish className="w-3 h-3" /> Fish here
                  </Sticker>
                  <div className="font-display text-[20px] leading-tight mt-2 drop-shadow-[0_3px_12px_rgba(0,0,0,0.7)]">
                    {example.fishery.name}
                  </div>
                  <div className="mono text-[10px] uppercase tracking-wider text-white/90">
                    {(example.fishery.descriptors || [])[0] || "Fishing water"}
                  </div>
                  <div className="mono text-[10px] text-[#fde047] uppercase tracking-[0.22em] mt-2">
                    +{Math.max(0, (example.total || 1) - 1)} more within 10 km
                  </div>
                </div>
              </div>
            )}
          </Scene>

          {/* SCENE 3 — CTA */}
          <Scene
            label="Scene 3 · CTA"
            testid="fc-scene-3"
            bg="linear-gradient(180deg, #ff4500 0%, #0c4a6e 55%, #0e0e0e 100%)"
          >
            <div className="p-5 pt-7 text-center">
              <Sticker color="#ffffff" textColor="#0c4a6e">
                <Fish className="w-3 h-3" /> Find your pair
              </Sticker>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center">
              <div className="font-display text-[42px] leading-[0.95] drop-shadow-[0_3px_12px_rgba(0,0,0,0.7)]">
                22,000<br />pitches<br />🏕
              </div>
              <div className="font-display text-base leading-tight mt-3 text-[#fde047]">
                + every fishery within 10 km
              </div>
              <div className="mt-6 bg-white text-[#0c4a6e] inline-block px-4 py-3">
                <div className="font-display text-base leading-tight">camp-compass.uk</div>
                <div className="mono text-[10px] uppercase tracking-wider mt-1">All activities · all campers</div>
              </div>
              <div className="mt-5 mono text-[10px] uppercase tracking-[0.22em] text-white/95">
                Free to browse · no login needed
              </div>
            </div>
          </Scene>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-12 space-y-4">
        <Link
          to="/browse?has_fishery=1"
          className="block bg-[#0891b2] text-white text-center py-4 font-display text-lg hover:bg-[#0c4a6e] transition-colors"
          data-testid="fishing-camping-cta-find-pair"
        >
          Find a campsite near a fishery <ArrowRight className="inline w-4 h-4 ml-1" />
        </Link>

        <CopyBlock
          title="Caption · long (Instagram + Facebook)"
          body={captionLong}
          testid="fishing-camping-caption-long"
        />
        <CopyBlock
          title="Caption · short (Stories / TikTok)"
          body={captionShort}
          testid="fishing-camping-caption-short"
        />
      </div>
    </div>
  );
}
